import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { theme } from '@resistapp/client/components/shared/theme';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { useDeleteLabWorkbook } from '../../../hooks/api';

type DeleteLabWorkbookControlProps = {
  workbookId: string;
  workbookName?: string | null;
  disabled?: boolean;
  reasons?: string[];
  onDeleted?: () => void;
};

export function DeleteLabWorkbookControl({
  workbookId,
  workbookName,
  disabled = false,
  reasons = [],
  onDeleted,
}: DeleteLabWorkbookControlProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const queryClient = useQueryClient();
  const mutation = useDeleteLabWorkbook();
  const isSubmitting = mutation.isPending;
  const [deleteCompleted, setDeleteCompleted] = useState(false);
  const deleteButtonStyles = {
    bg: theme.colors.red900,
    color: 'white',
    _hover: { bg: theme.colors.red800 },
    _active: { bg: theme.colors.red700 },
    _disabled: { bg: theme.colors.red900, color: 'white', opacity: 0.5, cursor: 'not-allowed' },
  } as const;

  const openDialog = () => {
    if (disabled) {
      return;
    }
    setDeleteCompleted(false);
    onOpen();
  };

  const runDelete = async () => {
    if (isSubmitting || deleteCompleted) {
      return;
    }
    try {
      await mutation.mutateAsync({ workbookId });
      setDeleteCompleted(true);
      await queryClient.invalidateQueries({ queryKey: ['labWorkbook', workbookId] });
      toast({
        title: 'Workbook deleted',
        description: 'The lab workbook was removed.',
        status: 'success',
      });
      onClose();
      onDeleted?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Delete failed. Please try again.';
      toast({
        title: 'Delete failed',
        description: message,
        status: 'error',
      });
    }
  };

  const disabledReason = reasons.length > 0 ? reasons.join(' ') : 'Delete unavailable.';
  const dialogLocked = isSubmitting || deleteCompleted;

  return (
    <>
      <Tooltip label={disabledReason} isDisabled={!disabled} hasArrow>
        <Button
          size="sm"
          {...deleteButtonStyles}
          onClick={openDialog}
          isDisabled={disabled || isSubmitting || deleteCompleted}
          isLoading={isSubmitting}
          loadingText="Deleting"
        >
          Delete Workbook
        </Button>
      </Tooltip>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete workbook
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={3}>
                This will permanently delete the workbook and its gene selections, samples, uploads and results.
              </Text>
              <Text mb={3}>
                Deletion is until first samples have gone through QA and or included on other workbooks.
              </Text>
              <Text>Continue deleting{workbookName ? ` “${workbookName}”` : ''}?</Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} mr={3} isDisabled={dialogLocked}>
                Cancel
              </Button>
              <Button
                onClick={() => void runDelete()}
                isDisabled={dialogLocked}
                isLoading={isSubmitting && !deleteCompleted}
                {...deleteButtonStyles}
              >
                {deleteCompleted ? 'Deleted' : 'Delete'}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
